import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Chronopost from "~/assets/img/pickup_services/Chronopost.svg";
import Colissimo from "~/assets/img/pickup_services/Colissimo.svg";
import MondialRelay from "~/assets/img/pickup_services/MondialRelay.svg";
import { AddressView } from "~/components/ui/address-view";
import { CopyIcon } from "~/components/ui/icons/copy";
import { toast } from "~/components/ui/toast";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import type { OrderQuery, PickupAddress } from "~/graphql/graphql-new.gen";
import { PickupPointNetwork } from "~/graphql/graphql-new.gen";
import { useCopyToClipboard } from "~/hooks/use-copy-to-clipboard";
import { track } from "~/libs/analytics";
import { getFormattedAddress } from "~/utils/address";
import { capitalizeFirstLetter } from "~/utils/string";

type OrderPickupPointProps = {
  order: OrderQuery["order"];
};

export const OrderPickupPoint: React.FC<OrderPickupPointProps> = ({
  order,
}: OrderPickupPointProps) => {
  const { t, i18n } = useTranslation();

  const [pickupPoint] = useState(order.shipping.pickupPoint);

  const [pickupIdClipboardState, copyPickupIdToClipboard] =
    useCopyToClipboard();
  const [addressClipboardState, copyAddressToClipboard] = useCopyToClipboard();

  useEffect(() => {
    const { value, error } = pickupIdClipboardState;
    if (value) {
      toast.success(t("order.copy_pickup_id_feedback"));
    }
    if (error) {
      Sentry.captureException(error);
    }
  }, [pickupIdClipboardState, t]);

  useEffect(() => {
    const { value, error } = addressClipboardState;
    if (value) {
      toast.success(t("order.copy_address_feedback"));
    }
    if (error) {
      Sentry.captureException(error);
    }
  }, [addressClipboardState, t]);

  if (!pickupPoint) return null;

  const onCopyPickupId = (pickupId: string) => {
    copyPickupIdToClipboard(pickupId);
    track("CopyToClipboard", {
      label: "pickup_id",
    });
  };

  const onCopyAddress = async (address: PickupAddress) => {
    let stringToCopy = "";
    await getFormattedAddress(
      {
        ...address,
        company: address.name,
        zip: address.zipCode,
        address1: address.street,
        address2: address.streetAdditional ?? "",
        country: address.countryCode,
      },
      i18n.language === "fr" ? "FR" : "EN",
    ).then((address) => {
      address.forEach((row) => (stringToCopy += `${row}\n`));
    });
    copyAddressToClipboard(stringToCopy);
    track("CopyToClipboard", {
      label: "pickup_address",
    });
  };

  return (
    <>
      {pickupPoint.network ? (
        <section className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t("pickup_point.service_provider")}
          </h4>
          <p className="flex items-center gap-x-1">
            {pickupPoint.network === PickupPointNetwork.MondialRelay ? (
              <img src={MondialRelay} alt="" />
            ) : null}
            {pickupPoint.network === PickupPointNetwork.Colissimo ? (
              <img src={Colissimo} alt="" />
            ) : null}
            {pickupPoint.network === PickupPointNetwork.Shop2Shop ? (
              <img src={Chronopost} alt="" />
            ) : null}
            <span className="text-sm text-gray-700">
              {pickupPoint.network === PickupPointNetwork.Shop2Shop
                ? "Shop2Shop by Chronopost"
                : pickupPoint.network
                    .split("_")
                    .map((string) =>
                      capitalizeFirstLetter(string.toLowerCase()),
                    )
                    .join(" ")}
            </span>
          </p>
        </section>
      ) : null}
      {pickupPoint.id ? (
        <section className="space-y-2">
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t("pickup_point.pickup_point_id")}
          </h4>
          <div className="flex items-start justify-between">
            <p className="text-sm text-gray-700">{pickupPoint.id}</p>
            <Tooltip>
              <TooltipTrigger
                onClick={() => onCopyPickupId(pickupPoint.id)}
                type="button"
                className="rounded pt-1 transition-colors hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-gray-900/30"
              >
                <CopyIcon className="size-4" />
              </TooltipTrigger>
              <TooltipContent side="left">
                <div className="px-1.5 py-3">
                  {t("order.copy_pickup_id_tooltip")}
                </div>
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </div>
        </section>
      ) : null}
      {pickupPoint.address ? (
        <section className="space-y-2">
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t("pickup_point.address")}
          </h4>
          <div className="flex items-start justify-between">
            <AddressView
              label={t("address.label")}
              size="medium"
              address={{
                ...pickupPoint.address,
                street2: pickupPoint.address.streetAdditional ?? undefined,
                bp: pickupPoint.address.zipCode,
                country: "",
              }}
              locale={i18n.language === "fr" ? "FR" : "EN"}
            />
            <Tooltip>
              <TooltipTrigger
                onClick={() =>
                  onCopyAddress(pickupPoint.address as PickupAddress)
                }
                type="button"
                className="rounded pt-1 transition-colors hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-gray-900/30"
              >
                <CopyIcon className="size-4" />
              </TooltipTrigger>
              <TooltipContent side="left">
                <div className="px-1.5 py-3">
                  {t("order.copy_address_tooltip")}
                </div>
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </div>
        </section>
      ) : null}
    </>
  );
};
